import { IErrors } from './error';
import { IUserInfo } from './user';

export enum IFeatureType {
  ProductFeature = 'ProductFeature',
  Addon = 'Addon',
  NoOfLicenses = 'NoOfLicenses',
  DiscountPercent = 'DiscountPercent',
  DiscountAmount = 'DiscountAmount',
  TaxPercent = 'TaxPercent',
  TaxAmount = 'TaxAmount',
  General = 'General',
}

export enum SubscriptionType {
  Free = 'Free',
  Standard = 'Standard',
  Premium = 'Premium',
}

export enum SubscriptionTypeFilter {
  Free = 'Free',
  Standard = 'Standard',
  Premium = 'Premium',
  None = 'None',
}

export interface IFeatureInfo {
  Id: string;
  Name: string;
  Description?: string;
  FeatureType: IFeatureType;
}

export interface IProductInfo {
  Id: string;
  Name: string;
  Features: IFeatureInfo[];
}

export interface IPlanFeatureInfo {
  FeatureId: string;
  FeatureName: string;
  FeatureDescription?: string;
  FeatureType: IFeatureType;
  Value?: string;
  DisplayOrder?: number;
  CreatedBy: string;
  CreatedAt: string;
  UpdatedAt: string;
  UpdatedBy: string;
}

export interface IAddonInfo {
  Id: string;
  FeatureId: string;
  Price?: number;
}

export interface ISubscriptionPlanInfo {
  Id: string;
  Name: string;
  Currency: string;
  Price: number;
  BillingCycle: string;
  TrialDays?: number;
  IsActive?: boolean;
  IsAutoRenewing?: boolean;
  Products: IProductInfo[];
  Features: IPlanFeatureInfo[];
  Addons: IAddonInfo[];
  FinalAmount: number;
  FinalAmountWithoutDiscount: number;
  AppPlanId?: string;
}

export interface ISubscriptionPlanWithTaxAndDiscountInfo
  extends ISubscriptionPlanInfo {
  GST: number;
  OtherTax: number;
  Discount: number;
  CouponDiscount: number;
  Promocode: number;
  FinalAmount: number;
  FinalAmountWithoutDiscount: number;
  SubscriptionType: SubscriptionType;
  AllowedUsers: number;
  OrderId?: string;
  SubscriptionId?: string;
  EndDate?: string;
  AvailableActions?: string[];
  AutoRenewalStatus?: string;
}

export interface ICreateAddressRequest {
  AddressId?: string;
  Line1: string;
  Line2?: string;
  FullName?: string; // Full Name
  City: string;
  State: string;
  PostalCode: string;
  Country: string;
  GSTIN?: string; // GST Field
}

export interface IAddressInfo extends ICreateAddressRequest {
  AddressId: string;
}

export interface ICreateOrderRequest {
  GenerateUPILink: boolean;
  SubscriptionPlanId: string;
  OldSubscriptionId?: string;
  AutoRenewPlanId?: string;
  Amount: number;
  GST: number;
  OtherTax: number;
  Discount: number;
  CouponDiscount?: number;
  PromoCode?: string;
  TotalAmount: number;
  BillingAddressId?: string;
  ShippingAddressId?: string;
  PublicNotes?: string;
  InternalNodes?: string;
  Currency?: string;
  BillingAddress?: ICreateAddressRequest;
  ShippingAddress?: ICreateAddressRequest;
}

export interface ISaveOrderResponse {
  OrderId: string;
  PaymentOrderId?: string;
  UPIPaymentLink?: string;
  ExpireBy?: string;
  Status?: string;
}

export enum OrderStatus {
  Draft = 'Draft',
  InProgress = 'InProgress',
  OnHold = 'OnHold',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Returned = 'Returned',
}

export enum PaymentStatus {
  PaymentPending = 'PaymentPending',
  PaymentFailed = 'PaymentFailed',
  PartiallyPaid = 'PartiallyPaid',
  Paid = 'Paid',
}

export enum ReturnStatus {
  ReturnInitiated = 'ReturnInitiated',
  ReturnRejected = 'ReturnRejected',
  ItemsReceived = 'ItemsReceived',
  RefundInitiated = 'RefundInitiated',
  RefundFailed = 'RefundFailed',
  Refunded = 'Refunded',
}

export interface IGetAllOrdersRequest {
  PageNumber: number;
  PageSize: number;
  OrderStatus?: OrderStatus;
  PaymentStatus?: PaymentStatus;
  Search?: string;
  PlanId?: string;
  DateFrom?: string;
  DateTo?: string;
}
export interface ISubscriptionInfo {
  Id: string;
  SubscriptionType?: string;
  Rights?: string[];
  AllowedUsers: number;
  StartDate: string;
  EndDate: string;
  CreatedAt: string;
  UpdatedAt: string;
  Status?: string;
  SubscriptionPlanId?: string;
  BillingCycle?: string;
}

export interface IOrderInfo {
  OrderId: string;
  Name?: string;
  UserId: string;
  User?: IUserInfo;
  SubscriptionPlanId: string;
  Amount: number;
  GST: number;
  OtherTax: number;
  Discount: number;
  CouponDiscount?: number;
  PromoCode?: string;
  TotalAmount: number;
  AmountPaid: number;
  AmountPending: number;
  Status: OrderStatus;
  PaymentStatus: PaymentStatus;
  ReturnStatus: ReturnStatus;
  BillingAddressId?: string;
  ShippingAddressId?: string;
  PaymentOrderId?: string;
  RefundId?: string;
  ApprovedRefundAmount?: number;
  RefundedAmount?: number;
  PublicNotes?: string;
  InternalNodes?: string;
  CreatedAt: string;
  UpdateAt: string;
  BillingAddress: IAddressInfo;
  ShippingAddress: IAddressInfo;
  InvoiceSent: boolean;
  InvoiceGenerated: boolean;
  PurchaseDate?: string;
  Subscription?: ISubscriptionInfo;
  Currency?: string;
}
export interface IOrderInfoPagedResult {
  PageNumber: number;
  PageSize: number;
  RecordsInPage: number;
  TotalPages: number;
  TotalCount: number;
  Value: IOrderInfo[];
  Errors: IErrors[];
}
export interface ICancelAutoRenewingSubscription {
  orderId: string;
  cancelAtCycleEnd?: boolean;
}

export interface ISaveOrderResponseResult {
  OrderId: string;
  IsPaymentOrder: boolean;
  PaymentLinkId?: string;
  PaymentOrderId?: string;
  UPIPaymentLink?: string;
  ExpireBy?: string;
  Status?: string;
}
export interface ICancelOrderRequest {
  OrderId: string;
}

export enum PlanButtonLabel {
  Subscribe = 'Subscribe Now',
  Buy = 'Buy Now',
  Change = 'Change Plan',
  Renew = 'Renew Plan',
  Complete = 'Complete Payment',
  OrderPending = 'Order Pending',
  Active = 'Active',
  Cancel = 'Cancel',
}

export interface IFulfillOrderRequest {
  OrderId: string;
  SendEmail: boolean;
}

export interface IFulfillOrderResponse {
  OrderId: string;
  SubscriptionId: string;
  Message?: string;
}

export interface IUpdateSubscriptionRequest {
  StartDate?: string;
  EndDate?: string;
  AllowedUsers?: number;
}
